import { useState, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Card, SectionTitle, Alert, btnPrimary, btnSecondary } from "../ui-bits";
import api, { fmtTimeLong, apiErr } from "../../lib/api";
import { connectYoutubePopup } from "../../lib/youtubeOauth";
import { toast } from "sonner";
import { Download, Copy, FileText, Check, Youtube, Loader2, Upload, Unplug, CalendarClock, Image as ImageIcon, Sparkles, Eye, ThumbsUp, MessageCircle, UserPlus, Timer, PieChart } from "lucide-react";
import ShortsCard from "../ShortsCard";
import RetentionPanel from "../RetentionPanel";

const fmtCompact = (n) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(n || 0);
const fmtWatchTime = (minutes) => {
  const m = minutes || 0;
  return m >= 60 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)} min`;
};

export default function TabDescarga({ project, setTab }) {
  const { t } = useTranslation();
  const out = project?.render?.output;

  if (!out) {
    return <Alert type="warning" testid="download-not-ready">{t("dl.notReady")}</Alert>;
  }

  const downloadText = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(t("dl.downloading", { name: filename }));
  };

  return (
    <div className="space-y-6">
      <SectionTitle sub={t("dl.subtitle")}>{t("dl.title")}</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-xl font-bold font-head text-slate-900 mb-4">{t("dl.preview")}</h3>
          <video data-testid="video-player" src={out.video_url} poster={out.thumbnail} controls className="w-full rounded-lg border-2 border-slate-200 bg-black aspect-video" />
          <div className="grid grid-cols-3 gap-3 mt-4 text-center">
            <Stat label={t("dl.duration")} value={fmtTimeLong(out.duration)} />
            <Stat label={t("dl.resolution")} value="1920×1080" />
            <Stat label={t("dl.size")} value={`${out.size_mb} MB`} />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <a data-testid="download-video-button" href={out.video_url} download="final.mp4" target="_blank" rel="noreferrer" className={`flex-1 ${btnPrimary}`}>
              <Download size={22} /> {t("dl.downloadMp4")}
            </a>
            <button data-testid="download-srt-button" onClick={() => downloadText(out.srt, "final.srt")} className={btnSecondary}>
              <FileText size={22} /> {t("dl.downloadSrt")}
            </button>
          </div>
        </Card>

        <YoutubeSection project={project} out={out} setTab={setTab} />
      </div>

      <ShortsCard project={project} />
    </div>
  );
}

function YoutubeSection({ project, out, setTab }) {
  const { t } = useTranslation();
  const [yt, setYt] = useState(null); // null=loading | {connected}
  const [copied, setCopied] = useState("");
  const [title, setTitle] = useState(project?.name || "");
  const [description, setDescription] = useState(out?.description || "");
  // Público por defecto: es lo que quiere quien llega hasta aquí, y dejarlo en privado obliga a
  // un clic extra en todos los vídeos para corregir un valor que casi nunca es el deseado.
  // La excepción es no tener miniatura: subir público sin miniatura entrega el vídeo con un
  // fotograma al azar de portada, y en YouTube la portada decide si alguien entra. En ese caso
  // se deja en privado, que es reversible desde YouTube en dos clics, y el aviso de arriba ya
  // explica que falta la miniatura.
  const [privacy, setPrivacy] = useState(
    () => (project?.custom_thumbnail?.url ? "public" : "private"));
  const [schedule, setSchedule] = useState(false);
  const [publishAt, setPublishAt] = useState("");
  const [aiDisclosure, setAiDisclosure] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [upload, setUpload] = useState(null);
  const [stats, setStats] = useState(null);
  const thumbUrl = project?.custom_thumbnail?.url || "";
  const pollRef = useRef();
  const hasThumb = !!thumbUrl;
  // El valor inicial se fija al montar, así que no cubre el caso de estar en esta pestaña cuando
  // TERMINA la miniatura (se genera desde el paso anterior y llega por sondeo). Aquí se corrige,
  // pero SOLO si el usuario no ha elegido nada todavía: una vez ha tocado los botones, su
  // elección manda — cambiarle la privacidad por debajo sería peor que dejarla mal.
  const privacidadTocada = useRef(false);
  useEffect(() => {
    if (hasThumb && !privacidadTocada.current) setPrivacy("public");
  }, [hasThumb]);

  const loadStatus = async () => {
    try { const { data } = await api.get("/youtube/status", { params: { channel_id: project.channel_id } }); setYt(data); }
    catch (e) { setYt({ connected: false }); }
  };
  const loadUpload = async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/youtube/upload-status`);
      setUpload(data);
      if (data.status === "uploading") { clearTimeout(pollRef.current); pollRef.current = setTimeout(loadUpload, 4000); }
    } catch (e) {}
  };
  useEffect(() => { loadStatus(); loadUpload(); return () => clearTimeout(pollRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => {
    api.get(`/projects/${project.id}/calendar-entry`).then((r) => {
      const d = r.data?.date;
      if (d && d >= new Date().toISOString().slice(0, 10)) { setSchedule(true); setPublishAt(`${d}T12:00`); }
    }).catch(() => {});
  }, [project.id]);

  // Estadísticas reales del vídeo recién subido (misma API que Mis Vídeos). Justo tras subir,
  // YouTube tarda un poco en propagar vistas/likes y sobre todo subscribers_gained (Analytics
  // API, más lenta) — es normal ver ceros los primeros minutos, no es un fallo de la llamada.
  useEffect(() => {
    if (upload?.status !== "done" || !upload.video_id || !project?.channel_id) { setStats(null); return; }
    api.get(`/channels/${project.channel_id}/youtube/video-stats`, { params: { video_ids: upload.video_id } })
      .then(({ data }) => setStats(data[upload.video_id] || null))
      .catch(() => setStats(null));
  }, [upload?.status, upload?.video_id, project?.channel_id]);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); }
    catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(key); setTimeout(() => setCopied(""), 1500);
    toast.success(t("dl.copied"));
  };
  const connect = async () => {
    setConnecting(true);
    try {
      await connectYoutubePopup(project.channel_id);
      toast.success(t("dl.connected"));
      await loadStatus();
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setConnecting(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(t("dl.disconnectConfirm"))) return;
    try { await api.post("/youtube/disconnect", null, { params: { channel_id: project.channel_id } }); setYt({ connected: false }); toast.success(t("dl.disconnected")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doUpload = async () => {
    const body = {
      title: title.trim(), description, privacy: schedule ? "private" : privacy,
      contains_synthetic_media: aiDisclosure,
    };
    if (schedule) {
      if (!publishAt) { toast.warning(t("dl.chooseDateTime")); return; }
      body.publish_at = new Date(publishAt).toISOString();
    }
    try {
      await api.post(`/projects/${project.id}/youtube/upload`, body);
      setUpload({ status: "uploading" });
      toast.success(t("dl.uploadingToast"));
      clearTimeout(pollRef.current); pollRef.current = setTimeout(loadUpload, 3000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const inputCls = "w-full border-2 border-slate-300 rounded-lg px-3 py-2.5 text-base outline-none focus:border-red-500";
  const privacyLabels = { private: t("dl.private"), unlisted: t("dl.unlisted"), public: t("dl.public") };

  const thumbStatus = (
    <Card className="p-5" data-testid="thumbnail-status">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-32 aspect-video rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
          {hasThumb ? <img src={thumbUrl} alt={t("dl.thumbAlt")} className="w-full h-full object-cover" data-testid="yt-thumb-preview" /> : <ImageIcon size={26} className="text-slate-300" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <h4 className="text-base font-bold text-slate-900">{t("dl.thumbSectionTitle")}</h4>
          <p className="text-sm text-slate-500 mt-0.5">{hasThumb ? t("dl.thumbReady") : t("dl.thumbMissing")}</p>
        </div>
        <button data-testid="go-thumb-step" onClick={() => setTab("miniatura")} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700">
          <ImageIcon size={16} /> {hasThumb ? t("dl.editThumb") : t("dl.createThumb")}
        </button>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {thumbStatus}
      <Card className="p-6" data-testid="youtube-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Youtube className="text-red-600" size={26} />
          <h3 className="text-xl font-bold text-slate-900">{t("dl.ytTitle")}</h3>
        </div>
        {yt?.connected && (
          <button data-testid="yt-disconnect-button" onClick={disconnect} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-red-600"><Unplug size={15} /> {t("dl.disconnect")}</button>
        )}
      </div>

      {yt === null ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : !yt.connected ? (
        // ---------- NO CONECTADO: descripción + tags para copiar + conectar ----------
        <div className="space-y-5" data-testid="yt-not-connected">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-bold text-slate-900">{t("dl.ytDesc")}</h4>
              <button data-testid="copy-description-button" onClick={() => copy(description, "desc")} className="flex items-center gap-1 text-blue-600 font-semibold hover:text-blue-800">
                {copied === "desc" ? <Check size={18} /> : <Copy size={18} />} {t("dl.copy")}
              </button>
            </div>
            <textarea readOnly data-testid="description-text" value={description} className="w-full h-28 border-2 border-slate-200 rounded-lg p-3 text-base bg-slate-50 resize-none vb-scroll" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-bold text-slate-900">{t("dl.suggestedTags")}</h4>
              <button data-testid="copy-tags-button" onClick={() => copy((out.tags || []).join(", "), "tags")} className="flex items-center gap-1 text-blue-600 font-semibold hover:text-blue-800">
                {copied === "tags" ? <Check size={18} /> : <Copy size={18} />} {t("dl.copy")}
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="tags-list">
              {(out.tags || []).map((tg, i) => <span key={`${tg}-${i}`} className="bg-blue-100 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full">{tg}</span>)}
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <p className="text-base text-slate-600 mb-3"><Trans i18nKey="dl.publishHere" components={{ 0: <b /> }} /></p>
            <button data-testid="yt-connect-button" onClick={connect} disabled={connecting} className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 min-h-[48px] text-base rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60">
              {connecting ? <Loader2 className="animate-spin" size={20} /> : <Youtube size={22} />} {t("dl.connectYt")}
            </button>
          </div>
        </div>
      ) : upload?.status === "done" ? (
        <div data-testid="yt-upload-done" className="space-y-2.5 rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-4">
          <Alert type="success"><Trans i18nKey="dl.uploadedTitle" values={{ privacy: upload.privacy, scheduled: upload.publish_at ? t("dl.scheduledFor", { date: new Date(upload.publish_at).toLocaleString() }) : "" }} components={{ 0: <b /> }} /></Alert>
          {stats && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-semibold text-slate-600" data-testid="yt-upload-stats">
              <span className="flex items-center gap-1"><Eye size={15} /> {fmtCompact(stats.views)}</span>
              <span className="flex items-center gap-1"><ThumbsUp size={15} /> {fmtCompact(stats.likes)}</span>
              <span className="flex items-center gap-1"><MessageCircle size={15} /> {fmtCompact(stats.comments)}</span>
              {stats.subscribers_gained != null && (
                <span title={t("videos.subsGainedTip")} className="flex items-center gap-1 text-emerald-700">
                  <UserPlus size={15} /> {fmtCompact(stats.subscribers_gained)}
                </span>
              )}
              {stats.watch_time_minutes != null && (
                <span title={t("videos.watchTimeTip")} className="flex items-center gap-1">
                  <Timer size={15} /> {fmtWatchTime(stats.watch_time_minutes)}
                </span>
              )}
              {stats.avg_view_percentage != null && (
                <span title={t("videos.retentionTip")} className="flex items-center gap-1">
                  <PieChart size={15} /> {stats.avg_view_percentage}%
                </span>
              )}
            </div>
          )}
          <div className="border-t border-emerald-200/70 pt-3">
            <RetentionPanel projectId={project.id} />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <a href={upload.url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 min-h-[44px] px-5 text-base font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"><Youtube size={20} /> {t("dl.viewYt")}</a>
            <button onClick={() => setUpload({ status: "idle" })} className="text-blue-600 font-semibold hover:text-blue-800">{t("dl.uploadAgain")}</button>
          </div>
        </div>
      ) : (
        // ---------- CONECTADO: formulario de publicación ----------
        <div className="space-y-4" data-testid="yt-upload-form">
          <span className="text-sm text-slate-600"><Trans i18nKey="dl.connectedAs" values={{ channel: yt.channel_title || t("dl.yourChannel") }} components={{ 0: <b className="text-slate-900" /> }} /></span>
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("dl.labelTitle")}</label>
            <input data-testid="yt-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} className={inputCls} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-bold text-slate-700">{t("dl.labelDesc")}</label>
              <button data-testid="copy-description-button" onClick={() => copy(description, "desc")} className="flex items-center gap-1 text-blue-600 text-sm font-semibold hover:text-blue-800">{copied === "desc" ? <Check size={15} /> : <Copy size={15} />} {t("dl.copy")}</button>
            </div>
            <textarea data-testid="yt-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${inputCls} resize-none`} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="yt-ai-disclosure-toggle" type="checkbox" checked={aiDisclosure} onChange={(e) => setAiDisclosure(e.target.checked)} className="w-5 h-5 accent-red-600" />
            <span className="text-base font-semibold text-slate-800 flex items-center gap-1.5"><Sparkles size={18} className="text-red-600" /> {t("dl.aiDisclosure")}</span>
          </label>
          <p className="text-xs text-slate-500 -mt-2 ml-7">{t("dl.aiDisclosureHint")}</p>
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("dl.privacy")}</label>
            <div className="flex flex-wrap gap-2">
              {/* De más abierto a más cerrado. El primero es el que casi siempre se quiere, y
                  leerlos en ese orden evita elegir "privado" por estar el primero a mano. */}
              {["public", "unlisted", "private"].map((v) => {
                const locked = schedule || (v === "public" && !hasThumb);
                return (
                  <button key={v} data-testid={`yt-privacy-${v}`} disabled={locked} onClick={() => { privacidadTocada.current = true; setPrivacy(v); }}
                    title={v === "public" && !hasThumb ? t("dl.publicThumbHint") : ""}
                    className={`px-4 py-2 rounded-lg font-semibold border-2 ${(!schedule && privacy === v) ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"} ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>{privacyLabels[v]}</button>
                );
              })}
            </div>
            {!hasThumb && <p className="text-xs text-amber-700 mt-1.5"><Trans i18nKey="dl.noThumbHint" components={{ 0: <b />, 1: <b /> }} /></p>}
          </div>

          <label className={`flex items-center gap-2 ${hasThumb ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
            <input data-testid="yt-schedule-toggle" type="checkbox" disabled={!hasThumb} checked={schedule} onChange={(e) => setSchedule(e.target.checked)} className="w-5 h-5 accent-red-600" />
            <span className="text-base font-semibold text-slate-800 flex items-center gap-1.5"><CalendarClock size={18} className="text-red-600" /> {t("dl.schedulePub")}</span>
          </label>
          {schedule && (
            <input data-testid="yt-publishat-input" type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} className={`${inputCls} sm:w-72`} />
          )}
          {schedule && <p className="text-xs text-slate-500">{t("dl.scheduleHint")}</p>}

          {upload?.status === "error" && <Alert type="error">{t("dl.uploadError", { error: upload.error })}</Alert>}

          <button data-testid="yt-upload-button" onClick={doUpload} disabled={upload?.status === "uploading"} className={`${btnPrimary} w-full sm:w-auto px-6 bg-red-600 hover:bg-red-700`}>
            {upload?.status === "uploading" ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
            {upload?.status === "uploading" ? t("dl.uploading") : (schedule ? t("dl.scheduleYt") : t("dl.uploadYt"))}
          </button>
        </div>
      )}
      </Card>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-3">
      <div className="text-lg font-bold font-head text-slate-900">{value}</div>
      <div className="text-sm text-slate-600">{label}</div>
    </div>
  );
}
